import type { Feature, WatermarkPosition } from './types';

export const APP_NAME = 'Batch Image Editor';
export const APP_VERSION = '1.0.0';
export const APP_ID = 'tools' as const;

export const MAX_FILE_SIZE_MB = 100;
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

export interface ResizePreset {
  name: string;
  width: number;
  description: string;
}

export const PORTAL_PRESETS: ResizePreset[] = [
  { name: 'Rightmove', width: 1600, description: 'Optimised for UK Rightmove listings' },
  { name: 'Zoopla', width: 1920, description: 'Best quality for Zoopla galleries' },
  { name: 'Zillow', width: 2048, description: 'Recommended for Zillow property listings' },
  { name: 'MLS', width: 2000, description: 'Ideal for most MLS systems' },
];

export const MARKETING_PRESETS: ResizePreset[] = [
  { name: 'Website HD', width: 1920, description: 'Perfect for websites and brochures' },
  { name: 'Instagram Post', width: 1080, description: 'Square format for social posts' },
  { name: 'Vertical / Reel', width: 1080, description: 'Width target for vertical content' },
];

/**
 * 360° equirectangular panorama presets — strict 2:1 aspect ratio.
 * Sized to match common 360 tour viewers (Pannellum, Marzipano, Three.js).
 */
export const TOUR_360_PRESETS: ResizePreset[] = [
  { name: '8K Panorama', width: 8192, description: '8192 × 4096 — premium VR / WebXR' },
  { name: '6K Panorama', width: 6144, description: '6144 × 3072 — high quality tours' },
  { name: '4K Panorama', width: 4096, description: '4096 × 2048 — recommended for web tours' },
  { name: '2K Panorama', width: 2048, description: '2048 × 1024 — fast loading on mobile' },
];

export const ALL_PRESETS: ResizePreset[] = [...PORTAL_PRESETS, ...MARKETING_PRESETS, ...TOUR_360_PRESETS];

export const DEFAULT_TOUR_360_WIDTH = 4096;
export const DEFAULT_TOUR_360_QUALITY = 0.8;
export const DEFAULT_STANDARD_QUALITY = 0.92;
/** Tolerance when validating 2:1 panorama uploads (5%) */
export const TOUR_360_RATIO_TOLERANCE = 0.05;

export const WATERMARK_POSITIONS: { label: string; value: WatermarkPosition }[] = [
  { label: 'Bottom Right', value: 'bottom-right' },
  { label: 'Bottom Left', value: 'bottom-left' },
  { label: 'Top Right', value: 'top-right' },
  { label: 'Top Left', value: 'top-left' },
  { label: 'Centre', value: 'center' },
];

export const DEFAULT_WATERMARK_SCALE = 0.15;
export const DEFAULT_WATERMARK_OPACITY = 0.7;
export const DEFAULT_WATERMARK_MARGIN = 20;

export const LANDING_HEADLINE = 'Batch resize, watermark and rename property photos in seconds';
export const LANDING_SUBTEXT =
  'A free image utility from ReHub Studio for estate agents, photographers and property marketers. Resize images, add your logo, rename files and download everything in one click.';
export const LANDING_CTA = 'Start Free';

export const LANDING_FEATURES: Feature[] = [
  {
    title: 'Resize for any platform',
    description: 'Resize images for portals, websites and social media with one click. Choose presets or set a custom width.',
    icon: 'Maximize2',
  },
  {
    title: 'Add your logo in bulk',
    description: 'Upload your agency logo or watermark and apply it to every image. Control position, size and opacity.',
    icon: 'Stamp',
  },
  {
    title: 'Rename files neatly',
    description: 'Rename all files with a consistent pattern and auto-numbering for faster, cleaner uploads.',
    icon: 'FileEdit',
  },
];

export const ECOSYSTEM_APPS = [
  {
    name: 'Photoclear',
    tagline: 'Remove clutter and personal items with AI',
    url: 'https://photoclear.rehub.studio',
    color: 'violet',
    logo: 'https://photoclear.rehub.studio/Photoclear%20Logo%20HR%20Trans.png',
    logoWidth: 'w-[168px]',
  },
  {
    name: 'HDR Photo',
    tagline: 'Merge bracketed exposures into studio-quality HDR',
    url: 'https://hdrphoto.rehub.studio',
    color: 'blue',
    logo: 'https://hdrphoto.rehub.studio/hdr-photo-logo.png',
  },
  {
    name: 'PhotoEdit',
    tagline: 'Professional photo editing — free for life',
    url: 'https://photoedit.rehub.studio',
    color: 'blue',
    logo: 'https://photoedit.rehub.studio/photoedit-logo.png',
  },
  {
    name: 'Virtual Staging',
    tagline: 'Transform empty rooms into furnished interiors',
    url: 'https://staging.rehub.studio',
    color: 'violet',
    logo: 'https://staging.rehub.studio/Virtual%20Staging%20Studio%20Logo%20(500%20×%20300%20Px)%20-%202.png',
    logoWidth: 'w-[260px]',
    logoHeight: 'h-24',
  },
  {
    name: 'Video Motion',
    tagline: 'Polish raw walkthrough videos automatically',
    url: 'https://videomotion.rehub.studio',
    color: 'violet',
    logo: 'https://videomotion.rehub.studio/logo.png',
  },
  {
    name: 'ImageMotion',
    tagline: 'Turn photos into cinematic property videos',
    url: 'https://imagemotion.rehub.studio',
    color: 'blue',
    logo: 'https://imagemotion.rehub.studio/Logo.webp',
  },
  {
    name: 'PropertyCuts',
    tagline: 'Assemble photos and clips into a finished video',
    url: 'https://propertycuts.rehub.studio',
    color: 'violet',
    logo: 'https://propertycuts.rehub.studio/logo.png',
  },
  {
    name: 'Hyper Dusk',
    tagline: 'Turn day exteriors into stunning dusk shots',
    url: 'https://hyperdusk.rehub.studio',
    color: 'amber',
    logo: 'https://hyperdusk.rehub.studio/hyperdusk-logo-wordmark-alpha.png',
  },
  {
    name: '360 Tours',
    tagline: 'Build immersive virtual tours for listings',
    url: 'https://360tours.rehub.studio',
    color: 'blue',
    logo: 'https://360tours.rehub.studio/360tours-studio-logo-transparent.png',
    logoWidth: 'w-[168px]',
  },
  {
    name: 'Planos',
    tagline: 'Create interactive property presentations',
    url: 'https://planos.rehub.studio',
    color: 'amber',
    logo: 'https://planos.rehub.studio/Planos%20Logo%20Red.png',
  },
  {
    name: 'Sketchplan',
    tagline: 'Sketch-to-floor-plan generation',
    url: 'https://sketchplan.rehub.studio',
    color: 'amber',
    logo: 'https://sketchplan.rehub.studio/cropped-Sketchplan-Logo1-removebg-preview.webp',
  },
  {
    name: 'Showcase',
    tagline: 'Branded microsites, brochures and QR codes',
    url: 'https://showcase.rehub.studio',
    color: 'amber',
    logo: 'https://showcase.rehub.studio/Logo.png',
  },
  {
    name: 'ReHub Studio',
    tagline: 'Done-for-you property media service',
    url: 'https://studio.rehub.studio',
    color: 'violet',
    logo: 'https://studio.rehub.studio/studio-logo.png',
  },
];
